import React from 'react';
import { Search, X, Calendar as CalendarIcon } from 'lucide-react';
import { ORDER_TRACKING, SOURCE } from '../../lib/statusConfig';
import { ROLES } from '../../lib/routeConfig';

// The 3 "call/message" statuses grouped under the "En attente" alias shown in
// the dashboard donut. When the donut deep-links to /commandes, the URL carries
// `statut=call_1_message_1,call_2_message_2,call_3` and this filter row renders
// a single dismissible "En attente" pill instead of the status dropdown.
export const PENDING_STATUSES = ['call_1_message_1', 'call_2_message_2', 'call_3'];
function isPendingGroup(status) {
    if (!status || typeof status !== 'string') return false;
    const parts = new Set(status.split(',').map((s) => s.trim()).filter(Boolean));
    if (parts.size !== PENDING_STATUSES.length) return false;
    return PENDING_STATUSES.every((s) => parts.has(s));
}

// Search box + filter dropdowns row. Emits partial filter objects to onChange.
// Server-side application: parent turns these into query params.

function NativeSelect({ value, onChange, options, placeholder, testId, className = '', optionsUppercase = false }) {
    // When `optionsUppercase` is enabled, we uppercase the closed trigger
    // display only if a real value is selected (so the placeholder "Transporteur"
    // stays in its natural case when nothing is chosen). Individual real options
    // in the open popup already carry `uppercase` on their own class; the
    // placeholder option receives an inline `text-transform: none` to defeat
    // any inheritance from the parent <select> when a value IS selected.
    const triggerUppercase = optionsUppercase && !!value;
    return (
        <select
            data-testid={testId}
            value={value || ''}
            onChange={(e) => onChange(e.target.value || null)}
            className={`h-9 pl-3 pr-8 rounded-lg border border-[#E5E7EB] bg-white text-sm text-[#1A1D2E] focus:outline-none focus:border-[#4B3FD8] ${triggerUppercase ? 'uppercase' : ''} ${className}`}
        >
            <option
                value=""
                style={optionsUppercase ? { textTransform: 'none' } : undefined}
            >
                {placeholder}
            </option>
            {options.map((opt) => (
                <option
                    key={opt.value}
                    value={opt.value}
                    className={optionsUppercase ? 'uppercase' : ''}
                >
                    {opt.label}
                </option>
            ))}
        </select>
    );
}

function DateRangePopover({ dateFrom, dateTo, onChange }) {
    const [open, setOpen] = React.useState(false);
    const rootRef = React.useRef(null);
    React.useEffect(() => {
        function h(e) { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); }
        document.addEventListener('mousedown', h);
        return () => document.removeEventListener('mousedown', h);
    }, []);

    const [from, setFrom] = React.useState(dateFrom || '');
    const [to, setTo] = React.useState(dateTo || '');
    React.useEffect(() => { setFrom(dateFrom || ''); setTo(dateTo || ''); }, [dateFrom, dateTo]);

    const label = (dateFrom || dateTo)
        ? `${dateFrom || '...'} → ${dateTo || '...'}`
        : 'Plage personnalisée';

    return (
        <div ref={rootRef} className="relative inline-block">
            <button
                type="button"
                data-testid="filter-date-range-trigger"
                onClick={() => setOpen((v) => !v)}
                className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-[#E5E7EB] bg-white text-sm text-[#1A1D2E] hover:border-[#CBD2E0]"
            >
                <CalendarIcon size={14} strokeWidth={1.75} className="text-[#6B7280]" />
                {label}
            </button>
            {open && (
                <div className="absolute z-30 mt-2 p-4 bg-white border border-[#E5E7EB] rounded-lg shadow-lg w-[280px]">
                    <label className="block text-xs font-medium text-[#6B7280] mb-1">Du</label>
                    <input
                        type="date"
                        data-testid="filter-date-from"
                        value={from}
                        onChange={(e) => setFrom(e.target.value)}
                        className="w-full h-9 px-3 rounded-lg border border-[#E5E7EB] text-sm"
                    />
                    <label className="block text-xs font-medium text-[#6B7280] mb-1 mt-3">Au</label>
                    <input
                        type="date"
                        data-testid="filter-date-to"
                        value={to}
                        onChange={(e) => setTo(e.target.value)}
                        className="w-full h-9 px-3 rounded-lg border border-[#E5E7EB] text-sm"
                    />
                    <div className="flex gap-2 mt-4">
                        <button
                            type="button"
                            data-testid="filter-date-apply"
                            onClick={() => { onChange(from || null, to || null); setOpen(false); }}
                            className="flex-1 h-9 rounded-lg bg-gradient-to-r from-[#4B3FD8] to-[#9B7FF4] text-white text-sm font-medium"
                        >
                            Appliquer
                        </button>
                        <button
                            type="button"
                            onClick={() => { setFrom(''); setTo(''); onChange(null, null); setOpen(false); }}
                            className="h-9 px-3 rounded-lg border border-[#E5E7EB] text-sm text-[#1A1D2E]"
                        >
                            Effacer
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default function OrdersFilters({ filters, onChange, filterValues, user }) {
    const isAgent = user?.role === ROLES.CC_AGENT;

    const statusOptions = Object.entries(ORDER_TRACKING).map(([k, v]) => ({ value: k, label: v.label }));
    const sourceOptions = Object.entries(SOURCE).map(([k, v]) => ({ value: k, label: v.label }));
    const cityOptions = (filterValues?.cities || []).map((c) => ({ value: c, label: c }));
    const productOptions = (filterValues?.products || []).map((p) => ({ value: p, label: p }));
    const shippingOptions = (filterValues?.shipping || []).map((s) => ({ value: s, label: s }));
    const ccOptions = (filterValues?.call_centers || []).map((cc) => ({
        value: cc.id,
        label: cc.commercial_name ? `${cc.name} — ${cc.commercial_name}` : cc.name,
    }));

    const setFilter = (patch) => onChange({ ...filters, ...patch });

    const preset = filters.date_preset || '';

    return (
        <div className="flex flex-wrap items-center gap-2 mb-4">
            <div className="relative">
                <Search size={16} strokeWidth={1.75} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
                <input
                    data-testid="filter-search"
                    type="text"
                    value={filters.search || ''}
                    onChange={(e) => setFilter({ search: e.target.value || null })}
                    placeholder="Rechercher par nom, téléphone ou ID commande..."
                    className="w-[350px] h-9 pl-9 pr-3 rounded-lg border border-[#E5E7EB] bg-white text-sm text-[#1A1D2E] focus:outline-none focus:border-[#4B3FD8]"
                />
            </div>

            <NativeSelect
                testId="filter-status"
                value={isPendingGroup(filters.status) ? '' : (filters.status || '')}
                onChange={(v) => setFilter({ status: v })}
                options={statusOptions}
                placeholder={isPendingGroup(filters.status) ? 'En attente' : 'Tous les statuts'}
                className={isPendingGroup(filters.status) ? 'ring-2 ring-[#F59E0B]/40 border-[#F59E0B]/60' : ''}
            />

            {isPendingGroup(filters.status) && (
                <span
                    data-testid="filter-status-pending-pill"
                    className="inline-flex items-center gap-1 h-9 px-3 rounded-lg bg-[#F59E0B]/15 text-[#B45309] text-sm font-medium"
                    title="Regroupe Call 1 / Message 1, Call 2 / Message 2 et Call 3"
                >
                    En attente
                    <button
                        type="button"
                        data-testid="filter-status-pending-clear"
                        onClick={() => setFilter({ status: null })}
                        aria-label="Retirer le filtre En attente"
                        className="ml-1 -mr-1 p-0.5 rounded hover:bg-[#F59E0B]/25"
                    >
                        <X size={14} strokeWidth={2} />
                    </button>
                </span>
            )}

            <NativeSelect
                testId="filter-date-preset"
                value={preset}
                onChange={(v) => setFilter({ date_preset: v, date_from: null, date_to: null })}
                options={[
                    { value: 'today', label: "Aujourd'hui" },
                    { value: '7d', label: '7 derniers jours' },
                    { value: '30d', label: '30 derniers jours' },
                    { value: 'all', label: 'Tout' },
                ]}
                placeholder="Date"
            />

            <DateRangePopover
                dateFrom={filters.date_from}
                dateTo={filters.date_to}
                onChange={(f, t) => setFilter({ date_preset: null, date_from: f, date_to: t })}
            />

            {!isAgent && (
                <NativeSelect
                    testId="filter-call-center"
                    value={filters.call_center_id}
                    onChange={(v) => setFilter({ call_center_id: v })}
                    options={ccOptions}
                    placeholder="Tous les CC"
                />
            )}

            <NativeSelect
                testId="filter-city"
                value={filters.city}
                onChange={(v) => setFilter({ city: v })}
                options={cityOptions}
                placeholder="Ville"
            />
            <NativeSelect
                testId="filter-product"
                value={filters.product}
                onChange={(v) => setFilter({ product: v })}
                options={productOptions}
                placeholder="Produit"
            />
            <NativeSelect
                testId="filter-source"
                value={filters.source}
                onChange={(v) => setFilter({ source: v })}
                options={sourceOptions}
                placeholder="Source"
            />
            <NativeSelect
                testId="filter-shipping"
                value={filters.shipping_company}
                onChange={(v) => setFilter({ shipping_company: v })}
                options={shippingOptions}
                placeholder="Transporteur"
                optionsUppercase
            />

            {(filters.search || filters.status || filters.date_preset || filters.date_from || filters.date_to || filters.call_center_id || filters.city || filters.product || filters.source || filters.shipping_company) && (
                <button
                    type="button"
                    data-testid="filters-clear"
                    onClick={() => onChange({})}
                    className="inline-flex items-center gap-1 h-9 px-3 rounded-lg text-sm text-[#6B7280] hover:text-[#1A1D2E]"
                >
                    <X size={14} strokeWidth={1.75} />
                    Effacer
                </button>
            )}
        </div>
    );
}
